import React, { useState, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useAuth } from "./context/AuthContext";
import { db } from "./firebase";
import { doc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";

import { messaging } from "./firebase";
import { getToken, onMessage } from "firebase/messaging";

import LoginScreen from "./components/Auth/LoginScreen";
import SignupScreen from "./components/Auth/SignupScreen";
import ChatScreen from "./components/Chat/ChatScreen";
import ContactList from "./components/Chat/ContactList";
import Header from "./components/Header";
import AdminPanel from "./components/Admin/AdminPanel";

import VideoCall from "./components/Call/VideoCall";
import AudioCall from "./components/Call/AudioCall";
import IncomingCallPopup from "./components/Call/IncomingCallPopup";

import { connectSocket } from "./socket/signalingClient";

import "./App.css";

/* ---------------- WRAPPERS ---------------- */
function SignupWithInviteWrapper() {
  const { inviteCode } = useParams();
  return <SignupScreen inviteCode={inviteCode} />;
}

/* ---------------- APP ---------------- */
function App() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [selectedChat, setSelectedChat] = useState(null);

  // 📞 CALL STATE
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);

  const permissionAskedRef = useRef(false);

  /* ---------- FCM TOKEN ---------- */
  useEffect(() => {
    if (!currentUser || permissionAskedRef.current) return;
    permissionAskedRef.current = true;

    (async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        const token = await getToken(messaging, {
          vapidKey:
            "BLQdKvQDtHCUKidI-NPvk8aBcLqwm3VqIAx__pvatf_pILBANUK_zehoVcpgzuX5KbnU1YiDjO87ytTjsgNN1PU",
        });

        if (!token) return;

        await setDoc(
          doc(db, "users", currentUser.uid),
          {
            uid: currentUser.uid,
            email: currentUser.email || "",
            fcmToken: token,
            updatedAt: Date.now(),
          },
          { merge: true }
        );
      } catch (err) {
        console.error("❌ FCM ERROR", err);
      }
    })();
  }, [currentUser]);

  /* ---------- FOREGROUND FCM ---------- */
  useEffect(() => {
    if (!currentUser) return;
    return onMessage(messaging, () => {});
  }, [currentUser]);

  /* ---------- SOCKET CONNECTION ---------- */
  useEffect(() => {
    if (!currentUser?.uid) return;
    connectSocket(currentUser.uid);
  }, [currentUser]);

  /* ---------- INCOMING CALL LISTENER ---------- */
  useEffect(() => {
    if (!currentUser) return;

    const unsub = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
      const call = snap.data()?.incomingCall || null;
      if (activeCall) return;
      setIncomingCall(call);
    });

    return () => unsub();
  }, [currentUser, activeCall]);

  /* ---------- CHAT ---------- */
  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    navigate(`/chat/${chat.chatId}`);
  };

  /* ---------- ACCEPT CALL ---------- */
  const handleAcceptCall = async () => {
    if (!incomingCall?.roomId || !currentUser) return;

    await updateDoc(doc(db, "users", currentUser.uid), {
      incomingCall: null,
    });

    setIncomingCall(null);
    setActiveCall({
      roomId: incomingCall.roomId,
      type: incomingCall.type,
    });
  };

  /* ---------- REJECT CALL ---------- */
  const handleRejectCall = async () => {
    if (!currentUser) return;

    await updateDoc(doc(db, "users", currentUser.uid), {
      incomingCall: null,
    });

    setIncomingCall(null);
  };

  /* ---------- END CALL ---------- */
  const handleEndCall = () => {
    setActiveCall(null);
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="app-container">
      {/* GLOBAL HEADER */}
      {currentUser && (
        <Header selectedChat={selectedChat} currentUser={currentUser} />
      )}

      {/* MAIN CONTENT BELOW HEADER */}
      <div className="main-content">
        <Routes>
          {!currentUser ? (
            <>
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/signup" element={<SignupScreen />} />
              <Route
                path="/invite/:inviteCode"
                element={<SignupWithInviteWrapper />}
              />
              <Route path="*" element={<Navigate to="/login" />} />
            </>
          ) : (
            <>
              <Route
                path="/"
                element={
                  <ContactList
                    onSelectChat={handleSelectChat}
                    selectedChat={selectedChat}
                    currentUser={currentUser}
                  />
                }
              />
              <Route
                path="/chat/:chatId"
                element={
                  selectedChat ? (
                    <ChatScreen
                      selectedChat={selectedChat}
                      currentUser={currentUser}
                    />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route path="/admin" element={<AdminPanel />} />
              
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
      </div>

      {/* 📞 INCOMING CALL POPUP */}
      {incomingCall && currentUser && !activeCall && (
        <IncomingCallPopup
          call={incomingCall}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      {/* 🎥 AUDIO/VIDEO CALL */}
      {activeCall && currentUser && (
        <>
          {activeCall.type === "video" ? (
            <VideoCall
              roomId={activeCall.roomId}
              userId={currentUser.uid}
              onClose={handleEndCall}
            />
          ) : (
            <AudioCall
              roomId={activeCall.roomId}
              userId={currentUser.uid}
              onClose={handleEndCall}
            />
          )}
        </>
      )}
    </div>
  );

}

export default App;
